import { useState } from "react";
import { Skeleton } from "@/ui/skeleton";
import { ICONES, Icone, s } from "../visual.jsx";
import { useAcoes, useCasoAtual, useEstado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import CabecalhoDaConversa from "./CabecalhoDaConversa.jsx";
import Trilha from "./Trilha.jsx";
import Redacao from "./Redacao.jsx";
import PalcoDaIA from "./PalcoDaIA.jsx";
import { Ausencia, Botao } from "./comuns.jsx";

/**
 * A conversa inteira: topo, faixa da IA, trilha e campo de escrever.
 *
 * Antes daqui saía UMA tela para cinco situações diferentes — "Carregando…",
 * para sempre. Link de atendimento é o que a casa mais manda por mensagem, e o
 * link que chega é justamente o do caso apagado, arquivado, de outro escritório
 * ou aberto depois que a sessão caiu. Agora cada uma diz o que é e por onde sair:
 * quem lê é `ausencia()`, quem pinta é `<Ausencia>`.
 */
/** A faixa de "este caso está fora da mesa", com a saída dentro dela. */
function FaixaDoArquivo({ id }) {
  const { desarquivarCaso } = useAcoes();
  return (
    <div className="flex flex-none flex-wrap items-center gap-2 border-b border-b-border bg-foreground/4 px-4 py-2 text-apoio text-tinta-suave">
      <Icone de={ICONES.arquivar} tamanho={14} />
      <span className="min-w-0 flex-1">Arquivado — fora da mesa e das pastilhas. Mandar mensagem traz o caso de volta.</span>
      <Botao rotulo="Trazer de volta" aoTocar={() => desarquivarCaso(id)} />
    </div>
  );
}

export default function Conversa({ empurraPainel, painelNaTela, listaNaTela, aoAlternarLista, aoAlternarPainel }) {
  const { caso, casoErro: falhaGuardada } = useEstado();
  const { carregarCaso, receberArquivos } = useAcoes();
  const { casoId, voltarParaLista, palcoAberto } = useNavegacao();
  const atual = useCasoAtual();
  const [arrastando, setArrastando] = useState(false);

  /* Só a falha DESTE caso: a do anterior ainda está no estado até o efeito de
     carregar rodar, e ela não fala sobre a conversa que está abrindo agora. */
  const casoErro = falhaGuardada?.id === casoId ? falhaGuardada : null;

  const moldura = (filho) => <div className="flex h-full overflow-y-auto p-[26px]">{filho}</div>;

  /* `/atendimentos/tal`: a rota casa, o id não é número, e ninguém chega a pedir
     nada ao servidor — a tela esperava para sempre um pedido que nunca saiu. */
  if (!casoId)
    return moldura(
      <Ausencia
        falha={{ status: 400, daCasa: true, mensagem: "Este endereço não traz o número do atendimento." }}
        aoVoltar={voltarParaLista}
      />,
    );

  if (casoErro)
    return moldura(
      <Ausencia
        falha={casoErro}
        aoVoltar={voltarParaLista}
        /* Tentar de novo só faz sentido em falha que passa (rede, prazo, 5xx):
           num 404 seria pedir o mesmo caso ausente uma segunda vez. */
        aoTentar={casoErro.status === 404 || casoErro.status === 403 ? undefined : () => carregarCaso(casoId)}
      />,
    );

  /*
   * Abrindo: o esqueleto da conversa, não a frase.
   *
   * "Abrindo o atendimento…" centralizado deixava a tela vazia por 300ms a cada
   * clique na lista, e a conversa aparecia de uma vez, deslocando tudo. O
   * esqueleto já tem a FORMA do que vem — bolha de um lado, bolha do outro — e a
   * troca deixa de ser um salto.
   */
  if (!atual || !caso)
    return (
      <div
        className="flex h-full flex-col gap-3.5 px-[22px] py-[26px]"
        role="status"
        aria-busy="true"
        /* O esqueleto não tem texto: quem lê por leitor de tela precisa do rótulo,
           senão a espera é silêncio absoluto. */
        aria-label="Abrindo o atendimento"
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            aria-hidden="true"
            /* Raio da bolha, porque é a forma da bolha que vem. Sem atraso por
               linha: a régua do `Esqueleto` da casa passou a ser uma onda só. */
            className={`max-w-[440px] rounded-superficie bg-foreground/8 ${i % 2 ? "self-end" : "self-start"}`}
            style={{ height: i % 3 === 0 ? 62 : 40, width: i % 2 ? "46%" : "62%" }}
          />
        ))}
      </div>
    );

  return (
    <div
      className="relative flex h-full min-h-0 flex-col"
      /*
       * Arrastar arquivo para cima da conversa anexa o arquivo.
       *
       * É o gesto que todo mundo tenta primeiro — e antes ele abria a foto no
       * navegador, jogando a mesa fora da tela. Vale na conversa INTEIRA e não só
       * no campo: quem arrasta mira na conversa, não num retângulo de 40px.
       */
      onDragOver={(e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        setArrastando(true);
      }}
      onDragLeave={(e) => {
        /* Só quando sai de verdade: `dragleave` dispara ao atravessar cada filho,
           e sem isto a moldura piscava a cada bolha por baixo do cursor. */
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setArrastando(false);
      }}
      onDrop={(e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault();
        setArrastando(false);
        receberArquivos(e.dataTransfer.files);
      }}
    >
      {arrastando && (
        <div
          className="pointer-events-none absolute inset-2 z-[9] flex items-center justify-center rounded-superficie border-2 border-dashed border-verde/55 bg-[color-mix(in_srgb,var(--secondary)_78%,transparent)]"
          style={s("animation:vFade var(--t-curta) var(--e-entra) both")}
        >
          <span className="rounded-xl bg-sidebar px-4 py-2.5 text-corpo font-bold text-sidebar-foreground shadow-[0_14px_34px_-12px_color-mix(in_oklab,_var(--color-foreground)_55%,_transparent)]">
            Solte para anexar à conversa
          </span>
        </div>
      )}
      {/* O atalho de pular subiu para a `Casca` em 09/09: aqui ele era o
          primeiro DA CONVERSA e o 44º DA PÁGINA — atrás dos 14 do trilho e dos
          19 da barra de filtro da lista (seis rostos, oito etapas do funil, o
          contador e o "etapas"). Atalho para pular que só se alcança depois de
          43 paradas não pula nada. */}
      <CabecalhoDaConversa
        caso={atual}
        empurraPainel={empurraPainel}
        painelNaTela={painelNaTela}
        listaNaTela={listaNaTela}
        aoAlternarLista={aoAlternarLista}
        aoAlternarPainel={aoAlternarPainel}
      />
      {/*
       * ARQUIVADO diz na CONVERSA, não só na linha da lista.
       *
       * Pela pastilha "Arquivados" se abre uma conversa que é idêntica a
       * qualquer outra da mesa — mesmo cabeçalho, mesma trilha, mesma barra de
       * escrever —, e responder cliente é o gesto de sempre. Sem uma faixa, a
       * única pista de que aquele caso está fora da mesa era a memória de quem
       * clicou na pastilha.
       *
       * Ela diz o que MANDAR MENSAGEM faz (traz de volta, e é o servidor que
       * decide isso em `whatsapp.enviar`), e não só que está arquivado: aviso
       * que não diz a consequência é aviso que se aprende a ignorar. E o botão
       * está aqui porque este é o lugar onde a pessoa está quando descobre.
       */}
      {caso?.registro?.arquivado ? <FaixaDoArquivo id={caso.registro.id} /> : null}
      <Trilha />
      <Redacao />
      {/*
       * Com a IA conduzindo, a conversa inteira vira o PALCO dela — cabeçalho,
       * trilha e barra de escrever por baixo. É o estado do atendimento e não um
       * extra: quem delegou e vê a conversa parada não tem como saber se o
       * modelo está trabalhando ou se o sistema travou. `?palco=0` (o botão "Ver
       * a conversa") devolve a mesa, e a faixa da barra de escrever segue lá.
       */}
      {caso?.delegacao && palcoAberto ? <PalcoDaIA caso={caso} /> : null}
    </div>
  );
}
